import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, merge, mergeMap, of, pipe, switchMap, tap } from "rxjs";
import { translate } from '@jsverse/transloco';

import { AppNotificationService } from "src/app/shared/services/notification.service";
import * as DeviceActions from "./device-actions";
import { DeviceService } from "src/app/devices/services/device.service";
import { IDeviceDetailsResponse } from "src/app/devices/models/device";
import { Router } from "@angular/router";
import { HttpResponse } from "@angular/common/http";
import { downloadBlob } from "src/app/shared/helpers/fileHelper";

@Injectable()
export class DeviceEffets {

    //////////////////
    // GET DEVICES //
    ////////////////

    // Get Devices
    getDevices$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeviceActions.getDevices), 
            mergeMap(() => {
            return this.deviceService.getDevices({pageNumber: 0, pageSize: 25}).pipe(
                map((response) => 
                    DeviceActions.getDevicesSuccess({ devices: response.data })
                ), 
                catchError((error) =>
                    of(DeviceActions.getDevicesFailure({ error: error.message }))
                )
            );
        })
        )
    );




    ///////////////////////
    // GET DEVICE BY ID //
    /////////////////////

    // Get Device by id
    getDeviceById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeviceActions.getDeviceById), 
            mergeMap((action) => {
            return this.deviceService.getDeviceById(action.id).pipe(
                map((response) => 
                    DeviceActions.getDeviceByIdSuccess({ device: response })
                ), 
                catchError((error) =>
                    of(DeviceActions.getDeviceByIdFailure({ error: error.message }))
                )
            );
        })
        )
    );




    ////////////////////////////
    // GET DEVICE BY COMPANY //
    //////////////////////////

    // Get Device by id
    getDevicesByCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeviceActions.getDevicesByCompany), 
            mergeMap((action) => {
            return this.deviceService.getDevicesByCompany({pageNumber: 0, pageSize: 25, companyId: action.companyId}).pipe(
                map((response) => 
                    DeviceActions.getDevicesByCompanySuccess({ devices: response.data })
                ), 
                catchError((error) =>
                    of(DeviceActions.getDevicesByCompanyFailure({ error: error.message }))
                )
            );
        })
        )
    );




    /////////////////////
    // CREATE DEVICE  //
    ///////////////////

    // Create Device
    createDevice$ = createEffect(() =>
      this.actions$.pipe(
          ofType(DeviceActions.createDevice),
          mergeMap((action) => 
              this.deviceService.createDevice(action.device).pipe(
                  map((device: IDeviceDetailsResponse) => DeviceActions.createDeviceSuccess({ device })),
                  catchError((error) => of(DeviceActions.createDeviceFailure({ error: error.message })))
              )
          )
      )
    );

    // Device created successfully
    createDeviceSuccess$ = createEffect(() => 
        this.actions$.pipe(
            ofType(DeviceActions.createDeviceSuccess),
            tap((response) => {
                // Get the current URL
                const currentUrl = this.router.url;

                // Find the position of the first "/"
                const baseUrl = currentUrl.substring(0, currentUrl.indexOf('/', 1))

                // Navigate to the modified URL
                this.router.navigate([baseUrl + "/" + response.device.id]).then(() => {
                    // Reload the page after navigation
                    window.location.reload();
                });
                this.appnotificationService.notification$.next({ message:translate('devices.createSuccess'), style: 'success' });
            })
        ), { dispatch: false }
    );

    // Device not created successfully
    createDeviceFailure$ = createEffect(() => 
      this.actions$.pipe(
          ofType(DeviceActions.createDeviceFailure),
          tap(() => {
              this.appnotificationService.notification$.next({ message:translate('devices.createFailed'), style: 'error' });
          })
      ), { dispatch: false }
    );




    /////////////////////
    // UPDATE DEVICE  //
    ///////////////////

    // Update Device
    updateDevice$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeviceActions.updateDevice),
            mergeMap((action) => 
                this.deviceService.updateDevice(action.device).pipe(
                    map((device: IDeviceDetailsResponse) => DeviceActions.updateDeviceSuccess({ device })),
                    catchError((error) => of(DeviceActions.updateDeviceFailure({ error: error.message })))
                )
            )
        )
    );
  
    // Update Device successful
    updateDeviceSuccess$ = createEffect(() => 
        this.actions$.pipe(
            ofType(DeviceActions.updateDeviceSuccess),
            tap(() => {
                // Success message
                this.appnotificationService.notification$.next({ message:translate('devices.updateSuccess'), style: 'success' });
            })
        ), { dispatch: false }
    );
  
    // Device not updated successfully
    updateDeviceFailure$ = createEffect(() => 
        this.actions$.pipe(
            ofType(DeviceActions.updateDeviceFailure),
            tap(() => {
                this.appnotificationService.notification$.next({ message:translate('devices.updateFailed'), style: 'error' });
            })
        ), { dispatch: false }
    );




    /////////////////////
    // DELETE DEVICE  //
    ///////////////////

    // Delete Device
    deleteDevice$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeviceActions.deleteDevice), 
            mergeMap((action) => {
                return this.deviceService.deleteDevice(action.id).pipe(
                    map(() => DeviceActions.deleteDeviceSuccess({id: action.id})), 
                    catchError((error) => of(DeviceActions.deleteDeviceFailure({ error: error.message })))
            )}
        ))
    );

    // Device deleted successfully
    deleteDeviceSuccess$ = createEffect(() => 
      this.actions$.pipe(
          ofType(DeviceActions.deleteDeviceSuccess),
          tap(() => {
            // Get the current URL
            const currentUrl = this.router.url;

            // Find the position of the first "/"
            const baseUrl = currentUrl.substring(0, currentUrl.indexOf('/', 1));

            // Navigate to the modified URL
            this.router.navigate([baseUrl]);
            
            this.appnotificationService.notification$.next({ message:translate('devices.deleteSuccess'), style: 'success' });
          })
      ), { dispatch: false }
    );

    // Device not deleted successfully
    deleteDeviceFailure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(DeviceActions.deleteDeviceFailure)
      ), { dispatch: false }
    );

    ///////////////////////////
    // IMPORT Devices       //
    /////////////////////////

    importDevices$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeviceActions.importDevices),
            mergeMap((action) => 
                this.deviceService.importDevices(action.formData)
                    .pipe(
                        map((response) => DeviceActions.importDevicesSuccess(response)),
                        catchError((error) => of(DeviceActions.importDevicesFailure({ error: error.message })))
                    )
            )
        )
    );

    importDevicesSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeviceActions.importDevicesSuccess),
            tap(() => {
                this.appnotificationService.notification$.next({ message:translate('devices.importSuccess'), style: 'success' });
            })
        ), { dispatch: false }
    );

    importDevicesFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeviceActions.importDevicesFailure),
            tap(() => {
                this.appnotificationService.notification$.next({ message:translate('devices.importFailed'), style: 'error' });
            })
        ), { dispatch: false }
    );


    constructor(
        private actions$: Actions, 
        private deviceService: DeviceService,
        private router: Router,
        private appnotificationService: AppNotificationService) {}
}